import * as React from "react";
import styled, { css } from "styled-components";
import SwiperCore, {
  Navigation,
  Autoplay
} from "swiper";
import {
  Swiper,
  SwiperSlide
} from "swiper/react";
import { useMappedState } from "redux-react-hook";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text/Text";
import { Icons } from "src/library/components/Icon/types";
import type { IStore } from "src/core/shared/store";
import { IProduct } from "src/core/products/api/products.api";
import { media } from "src/library/utils/styled-media-queries";
import { breakpoint } from "src/library/utils/styled-media-queries/index";

import { StandardProductCard } from "./ProductCard/StandardProductCard";

// install Swiper modules
SwiperCore.use([ Navigation, Autoplay ]);

interface FeaturedProductBannerProps {
  featuredProducts: IProduct[];
}

const FeaturedProductsBanner: React.ReactFCWithChildren<FeaturedProductBannerProps> = ({ featuredProducts }) => {
  const { role, settings } = useMappedState(({ global }: IStore) => ({
    role: global.role,
    settings: global.settings
  }));

  const [ cardActive, setCardActive ] = React.useState(false);

  const featuredProductsMemo = React.useMemo(() => {
    return featuredProducts || [];
  }, [ featuredProducts ]);

  return (
    <FeaturedProductsBannerWrapper>
      <Text
        as="h2"
        color="primary"
      >
        Featured
      </Text>

      <FeaturedProductsBannerSwiper
        spaceBetween={20}
        slidesPerView="auto"
        className={cardActive ? "active" : ""}
        navigation={{
          nextEl: ".swiper-next",
          prevEl: ".swiper-prev"
        }}
      >

        {featuredProductsMemo.map(featuredProduct => {
          return (
            <SwiperSlide key={`promotion-${featuredProduct.id}`}>
              <div className="card-inner-wrapper">
                <StandardProductCard
                  onCardActive={isOpen => setCardActive(isOpen)}
                  product={featuredProduct}
                  className={`featured-product-card ${cardActive ? "disabled" : ""}`}
                  productImage={featuredProduct.image}
                  currentRole={role}
                  installedModules={settings ? settings.installedModules : []}
                  featured
                />
              </div>
            </SwiperSlide>
          );
        })}

        <div className="swiper-navigation">
          <div className="swiper-nav-button swiper-prev">
            <div className="swiper-nav-button-inner">
              <Icon
                icon={Icons.CHEVRONLEFT}
                size={30}
                $fillColor="primaryDark"
              />
            </div>
          </div>

          <div className="swiper-nav-button swiper-next">
            <div className="swiper-nav-button-inner">
              <Icon
                icon={Icons.CHEVRONRIGHT}
                size={30}
                $fillColor="primaryDark"
              />
            </div>
          </div>
        </div>
      </FeaturedProductsBannerSwiper>
    </FeaturedProductsBannerWrapper>
  );
};

export default FeaturedProductsBanner;

const FeaturedProductsBannerWrapper = styled.div`
  ${({ theme }) => css`
    padding-bottom: ${theme.spacing.padding.md};
    padding-top: ${theme.spacing.padding.md};
    border-bottom: 2px solid ${theme.colors.primaryLight};
    width: 100%;
    display: none;
    background: ${theme.colors.lightGrey};
    padding-left: ${theme.spacing.padding.sm};
    padding-right: ${theme.spacing.padding.sm};
    margin-top: ${theme.spacing.margins.md};



    ${breakpoint("md", theme)} {
      padding-left: ${theme.spacing.padding.lg};
      padding-right: ${theme.spacing.padding.lg};
    }

    @media (hover: none) and (pointer: coarse) {
      display: none;
    }

    ${media({ gt: theme.breakpoints.lg })} {
      display: block;
    }
  `}
`;

const FeaturedProductsBannerSwiper = styled(Swiper)`
  ${({ theme }) => css`
    margin-top: 2rem;
    width: 100%;
    position: relative;
    height: 300px;
    z-index: 10;
    

    &.active {
      .featured-product-card.disabled:not(.active) {
        opacity: 0.5;
        position: relative;

        &:before {
          content: "";
          height: 100%;
          width: 100%;
          top: 0;
          left: 0;
          position: absolute;
        }
      }
    }

    .swiper-slide-active { 
      .featured-product-card {
        transform-origin: top  left;
      }
    }


    .card-inner-wrapper {
      position: relative;
      width: 200px;
      height: 300px;
      z-index: 0;

      &:hover {
        z-index: 10;
      }

    }
      
    .swiper-wrapper {
      display: flex;
      align-items: flex-start;
      height: 100%; 
    }

    .swiper-navigation {
      
      .swiper-nav-button {
        height: 100%;
        position: relative;
        width: 30px;
        
        &:after {
          content: "";
          height: 100%;
          width: 200px;
          position: absolute;
        }

        &.swiper-button-disabled {
          opacity: 0;
        }

        &:hover {
          cursor: pointer;
          
          .swiper-nav-button-inner {
            background: ${theme.colors.primaryDark};

            svg {
              fill: ${theme.colors.white}!important;
            }
          }
        }
      }

      .swiper-nav-button-inner {
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
        height: 50px;
        width: 50px;
        box-shadow: 0 0 10px rgba(0,0,0, 0.2);
        position: absolute;
        z-index: 1;
        top: 50%;
        transform: translateY(-50%);
        border-radius: 50%;
        border: 2px solid ${theme.colors.primaryDark};
      }

      .swiper-next {
        position: absolute;
        top: 0;
        right: 25px;

        &:after {
          left: 100%;
        }

        .swiper-nav-button-inner {
          right: -25px;
        }
      }

      .swiper-prev {
        top: 0;
        position: absolute;
        left: 25px;

        &:after {
          right: 100%;
        }

        .swiper-nav-button-inner {
          left: -25px
        }
      }
    }
  `}
`;
